import APIService from '../../../../Services/APIService';
import { Apis } from '../../../../Services/app.constants';
import { workspaceService } from '../../services/workspaceService';

function buildDownloadUrl(filterData = {}, selectedColumns = {}) {
  if (!filterData?.campaign_id || !filterData?.leads_form_id) {
    return null;
  }

  let url = `/${Apis.downloadB2b2cLeads}?campaign_id=${encodeURIComponent(filterData.campaign_id)}&leads_form_id=${encodeURIComponent(filterData.leads_form_id)}`;

  if (filterData.start_date && filterData.end_date) {
    url += `&start_date=${encodeURIComponent(filterData.start_date)}&end_date=${encodeURIComponent(filterData.end_date)}`;
  }

  if (Array.isArray(filterData.dynamicFilters) && filterData.dynamicFilters.length > 0) {
    url += `&data_filters=${encodeURIComponent(JSON.stringify(filterData.dynamicFilters))}`;
  }

  const selectedKeys = Object.keys(selectedColumns).filter((key) => selectedColumns[key]);
  if (selectedKeys.length > 0) {
    url += `&selected_columns=${encodeURIComponent(JSON.stringify(selectedKeys))}`;
  }

  return url;
}

function unwrapListResponse(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res)) return res;
  return res?.data?.list ?? [];
}

export const recordService = {
  async fetchRecords(workspaceId, tableId, params = {}) {
    return workspaceService.getTableRecords(workspaceId, tableId, params);
  },

  async fetchFormDefinition(formId) {
    return workspaceService.getLeadFormById(formId);
  },

  async fetchRecordById(mongoId) {
    if (!mongoId) throw new Error('Record ID is required');
    return APIService.get(`${Apis.insertLeadV1}?id=${mongoId}`);
  },

  async createRecord(payload) {
    return APIService.post(`${Apis.insertLeadV1}`, payload);
  },

  async updateRecord(payload) {
    return APIService.put(`${Apis.insertLeadV1}`, payload);
  },

  async deleteRecords(leadIds = []) {
    return APIService.remove(`${Apis.insertLeadV1}`, { lead_ids: leadIds });
  },

  async updateLeadStatus(payload) {
    return APIService.post(`${Apis.updateLeadStatus}?id=${payload?.id}`, { data: payload });
  },

  async bulkUpdateLeadStatus(payload) {
    if (!payload?.id) {
      return APIService.post(`${Apis.updateLeadStatus}`, payload);
    }
    return APIService.post(`${Apis.updateLeadStatus}?id=${payload.id}`, payload);
  },

  async moveRecords(payload) {
    return APIService.post('/v0/ui/leads/move_leads/', payload);
  },

  async saveFormColumnVisibility(formId, updatedFormPayload) {
    return APIService.put(`/v0/ui/leads/${formId}/edit_form`, updatedFormPayload);
  },

  async fetchStatusList(formId) {
    if (!formId) return [];

    try {
      const res = await APIService.get(
        `${Apis.statusSubStatus}?leads_form_id=${formId}&active=true&type_of_status=`
      );
      return unwrapListResponse(res);
    } catch {
      return [];
    }
  },

  async fetchAssignedUsers(orgId, campaignId = '') {
    if (!orgId) return [];

    try {
      const res = await APIService.get(
        `${Apis.getUserMinimalList}?org_id=${orgId}&campaign_id=${campaignId ?? ''}`
      );
      if (res?.status === 200 && Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    } catch {
      return [];
    }
  },

  async assignLeads(payload) {
    return APIService.post('/v0/ui/leads/assign_leads/', payload);
  },

  async unassignLeads(payload) {
    return APIService.post('/v0/ui/leads/unassign_leads/', payload);
  },

  async fetchFormsByCampaignId(campaignId) {
    if (!campaignId) return {};
    try {
      const res = await APIService.get(
        `v0/ui/leads/${encodeURIComponent(campaignId)}/form`
      );
      if (res?.status === 200 || res?.status === 201) {
        return res?.data && typeof res.data === 'object' ? res.data : {};
      }
      return {};
    } catch {
      return {};
    }
  },

  async downloadRecords(filterData = {}, selectedColumns = {}) {
    const url = buildDownloadUrl(filterData, selectedColumns);
    if (!url) {
      throw new Error('Campaign and form are required for download');
    }

    const filename = `Leads${filterData.campaign_id ?? ''}_${filterData.leads_form_id ?? ''}.xlsx`;
    return APIService.downloadFile(url, filename);
  },

  async postComment(recordId, comment) {
    if (!recordId) throw new Error('Record ID is required');
    if (!comment?.trim()) throw new Error('Comment is required');

    return APIService.post(`${Apis.getPostComment}?id=${recordId}`, {
      data: { comment: comment.trim(), id: recordId },
    });
  },

  async uploadImportFile(leadsFormId, formData) {
    if (!leadsFormId) throw new Error('Form ID is required');
    return APIService.post(
      `/v0/ui/leads/uploade-lead/?leads_form_id=${leadsFormId}`,
      formData,
      { 'Content-Type': 'multipart/form-data' }
    );
  },

  async downloadImportSampleFile(leadsFormId) {
    if (!leadsFormId) return;
    const url = `/v0/ui/leads/sample-file-lead-upload/?leads_form_id=${leadsFormId}`;
    return APIService.downloadFile(url, `Sample_file_${leadsFormId}.xlsx`);
  },

  async downloadImportErrorFile(formData) {
    return APIService.downloadFilePOST(
      '/v0/ui/leads/download-error-file/',
      formData,
      'lead_error_highlighted.xlsx'
    );
  },
};
